import { IMAGE_POSITION_OPTIONS } from "../../lib/image-position";
import { SliderWiggleField } from "../before-after/slider-wiggle.fields";
import { galleryItemKind } from "./gallery.kind";

const GalleryFields = {
  heading: {
    type: "custom",
    label: "Heading",
    metadata: {
      source: "ai",
      description:
        "Section heading that frames the work gallery (e.g. 'Recent Projects Around Austin')",
      seo: true,
      key: "gallery.heading",
    },
  },
  subheading: {
    type: "custom",
    label: "Subheading",
    metadata: {
      source: "ai",
      description: "Short intro that explains what visitors will see and reinforces trust",
      seo: true,
      key: "gallery.subheading",
    },
  },
  layout: {
    type: "radio",
    label: "Layout",
    options: [
      { label: "Grid", value: "grid" },
      { label: "Showcase", value: "showcase" },
      { label: "Masonry", value: "masonry" },
      // One row a visitor swipes through, with arrow buttons on bigger screens.
      { label: "Scrolling row", value: "strip" },
      // A grid where every Before & After item is a live slider, no click needed.
      { label: "Before & after sliders", value: "before-after" },
    ],
    metadata: {
      source: "manual",
    },
  },
  variant: {
    type: "radio",
    label: "Style",
    options: [
      { label: "Cards", value: "cards" },
      { label: "Editorial", value: "editorial" },
      // A dark band, the heading in big capitals, frameless photos with captions under them.
      { label: "Bold", value: "bold" },
    ],
    metadata: {
      source: "manual",
    },
  },
  columns: {
    type: "radio",
    label: "Columns",
    options: [
      { label: "2 Columns", value: "2" },
      { label: "3 Columns", value: "3" },
      { label: "4 Columns", value: "4" },
    ],
    metadata: {
      source: "manual",
    },
  },
  showCaptions: {
    type: "radio",
    label: "Show Captions",
    options: [
      { label: "Yes", value: "yes" },
      { label: "No", value: "no" },
    ],
    metadata: {
      source: "manual",
    },
  },
  showTags: {
    type: "radio",
    label: "Show Tags",
    options: [
      { label: "Yes", value: "yes" },
      { label: "No", value: "no" },
    ],
    metadata: {
      source: "manual",
    },
  },
  images: {
    type: "array",
    label: "Photos",
    metadata: {
      source: "context",
      contextKey: "business.stockImages",
      description: "Project or work photos shown in the gallery",
      seo: true,
      key: "gallery.images",
      // Offers "add every photo from the photo library" in the editor, so a
      // Gallery is seeded in one click and then edited photo by photo.
      fillFromPhotoLibrary: true,
      // Each item's settings are declared against its kind (metadata.kinds
      // Below); entries saved before the field are read from what they hold.
      inferKind: galleryItemKind,
      // Which setting holds the kind, so the editor can show an entry saved
      // Before the field as the type it is read as, rather than as nothing.
      kindField: "kind",
    },
    getItemSummary: (item: {
      kind?: string;
      caption?: string;
      tag?: string;
      alt?: string;
      photos?: unknown[];
      sourceProjectId?: string;
      beforeImageUrl?: string;
      afterImageUrl?: string;
    }) => {
      // Unnamed entries fall back to what they are, so a comparison never sits
      // In the list calling itself "Photo".
      const kind = galleryItemKind(item),
        fallback =
          kind === "before-after" ? "Before & After" : (kind === "project" ? "Photo set" : "Photo"),
        label = item.caption || item.tag || item.alt || fallback,
        extra = kind === "project" && Array.isArray(item.photos) ? item.photos.length : 0;
      if (extra > 0) {
        return `${label} (${extra + 1} photos)`;
      }
      return label;
    },
    arrayFields: {
      kind: {
        type: "radio",
        label: "Type",
        options: [
          { label: "Photo", value: "photo" },
          { label: "Before & After", value: "before-after" },
          { label: "Photo set", value: "project" },
        ],
        metadata: {
          source: "manual",
          description: "What this entry is. The settings below follow from it.",
        },
      },
      sourceProjectId: {
        type: "custom",
        label: "Synced Project",
        metadata: {
          source: "manual",
          kinds: ["project"],
          description:
            "Photos pull live from this project in your photo library. Unsync to manage them by hand.",
        },
      },
      imageUrl: {
        type: "custom",
        label: "Photo",
        metadata: {
          kinds: ["photo", "project"],
          description: "The photo shown in the grid. Also the cover when more photos are added.",
        },
      },
      photos: {
        type: "array",
        label: "More Photos",
        metadata: {
          kinds: ["project"],
          description:
            "Additional photos of this same project. Visitors page through these in the lightbox.",
        },
        getItemSummary: (item: { alt?: string }, index?: number) =>
          item?.alt || `Photo ${(index ?? 0) + 2}`,
        arrayFields: {
          imageUrl: {
            type: "custom",
            label: "Photo",
            metadata: {
              description: "Another photo of this project",
            },
          },
          caption: {
            type: "text",
            label: "Caption",
            metadata: {
              description:
                "Shown under this photo in the lightbox. Falls back to the project caption.",
              seo: true,
            },
          },
          alt: {
            type: "text",
            label: "Alt Text",
            metadata: {
              description: "Describe the work shown for accessibility and SEO",
              seo: true,
            },
          },
        },
        defaultItemProps: {
          imageUrl: "",
          caption: "",
          alt: "",
        },
      },
      alt: {
        type: "text",
        label: "Alt Text",
        metadata: {
          description: "Describe the work shown for accessibility and SEO",
          seo: true,
        },
      },
      caption: {
        type: "text",
        label: "Caption",
        metadata: {
          description: "Short caption describing the job or result",
          seo: true,
        },
      },
      position: {
        type: "select",
        label: "Image Focus",
        options: [...IMAGE_POSITION_OPTIONS],
        metadata: {
          source: "manual",
          kinds: ["photo", "project"],
          description: "Which part of the photo stays visible when the tile crops it",
        },
      },
      tag: {
        type: "text",
        label: "Tag",
        metadata: {
          advanced: true,
          description: "Small label such as Kitchen Remodel or Before and After",
        },
      },
      clickAction: {
        type: "radio",
        label: "When Clicked",
        options: [
          { label: "Opens its photos", value: "gallery" },
          { label: "Goes to a link", value: "link" },
        ],
        metadata: {
          source: "manual",
          advanced: true,
          description:
            "Whether the tile opens this project's photos in a lightbox or navigates to the link below.",
        },
      },
      href: {
        type: "text",
        label: "Link URL",
        metadata: {
          advanced: true,
          description: "Where the tile goes when it's set to 'Goes to a link'",
        },
      },
      beforeImageUrl: {
        type: "custom",
        label: "Before Image",
        metadata: {
          kinds: ["before-after"],
          description: "Optional comparison image used for the before state",
        },
      },
      afterImageUrl: {
        type: "custom",
        label: "After Image",
        metadata: {
          kinds: ["before-after"],
          description: "Optional comparison image used for the after state",
        },
      },
      beforeLabel: {
        type: "text",
        label: "Before Label",
        metadata: {
          kinds: ["before-after"],
          advanced: true,
          description: "Short label shown on the comparison card",
        },
      },
      afterLabel: {
        type: "text",
        label: "After Label",
        metadata: {
          kinds: ["before-after"],
          advanced: true,
          description: "Short label shown on the comparison card",
        },
      },
    },
    defaultItemProps: {
      kind: "photo",
      sourceProjectId: "",
      imageUrl: "",
      alt: "",
      caption: "",
      tag: "",
      position: "center",
      clickAction: "gallery",
      href: "",
      photos: [],
      beforeImageUrl: "",
      afterImageUrl: "",
      beforeLabel: "Before",
      afterLabel: "After",
    },
  },
  backgroundColor: {
    type: "text",
    label: "Background Color (optional)",
    metadata: {
      source: "manual",
    },
  },
  sliderWiggle: SliderWiggleField,
} as const;

export { GalleryFields };
